import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import {
  loadAccountPreference,
  saveAccountPreference,
  onAuthPreferenceRefresh,
} from "@/contexts/accountPreference";

interface SimpleModeContextType {
  isSimpleMode: boolean;
  toggleSimpleMode: () => void;
}

const SimpleModeContext = createContext<SimpleModeContextType>({
  isSimpleMode: false,
  toggleSimpleMode: () => {},
});

const STORAGE_KEY = "behtari_simple_mode";

/**
 * Simple Mode is persisted to `user_preferences.simple_mode` — its OWN column.
 *
 * Deliberately NOT `compact_view`, which looks like the obvious home for it and
 * is already owned by `PersonalizationContext` as a separate view-density
 * preference. See migration 20260904091000 for the reasoning.
 */
const DB_COLUMN = "simple_mode";

const isBool = (v: unknown): v is boolean => typeof v === "boolean";

export const SimpleModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSimpleMode, setIsSimpleMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // See FontSizeContext for the full rationale: localStorage paints instantly,
  // the account row is the source of truth and is what survives
  // `UserContext.logout`'s deliberate `localStorage.clear()`.
  // A COUNTER, NOT A LATCH — see FontSizeContext for the full account of why
  // the one-shot boolean silently defeated hydration on the sign-in path.
  const generationRef = useRef(0);

  const hydrateFromAccount = useCallback(async () => {
    const generation = ++generationRef.current;
    const read = await loadAccountPreference<boolean>(DB_COLUMN, isBool);
    // Superseded by a later hydrate or by a user toggle while we awaited.
    if (generationRef.current !== generation) return;
    // "absent" means the account was read fine and holds nothing, so the
    // default wins over a cached value that may belong to a previous user of
    // this device. "unavailable" leaves the current value untouched.
    if (read.status === "unavailable") return;
    const next = read.status === "value" ? read.value : false;
    setIsSimpleMode(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* non-fatal cache write */
    }
  }, []);


  useEffect(() => {
    void hydrateFromAccount();
    return onAuthPreferenceRefresh(
      () => { void hydrateFromAccount(); },
      // Invalidate any hydrate still in flight for the account being left, and
      // return to the default so the next person on this device does not
      // inherit it.
      () => { generationRef.current++; setIsSimpleMode(false); },
    );

  }, [hydrateFromAccount]);

  const toggleSimpleMode = useCallback(() => {
    setIsSimpleMode((prev) => {
      const next = !prev;
      generationRef.current++; // an explicit toggle outranks a pending hydrate
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* non-fatal cache write */
      }
      void saveAccountPreference(DB_COLUMN, next);
      return next;
    });
  }, []);

  return (
    <SimpleModeContext.Provider value={{ isSimpleMode, toggleSimpleMode }}>
      {children}
    </SimpleModeContext.Provider>
  );
};

export const useSimpleMode = () => useContext(SimpleModeContext);
